/* =====================================================================
 * Document AI — AI 명령별 쿼리 생성 유틸리티
 *
 * 기존 streamAgent(workspace_type: 'document')를 호출할 때
 * query 문자열을 구성하는 헬퍼 함수들
 * ===================================================================== */

import type { AICommandType } from '../types/document';

/* ------------------------------------------------------------------ */
/* 번역 언어 매핑                                                        */
/* ------------------------------------------------------------------ */

const LANG_NAMES: Record<string, string> = {
  en: '영어 (English)',
  ko: '한국어',
  ja: '일본어 (日本語)',
  zh: '중국어 (中文)',
  es: '스페인어 (Español)',
  fr: '프랑스어 (Français)',
  de: '독일어 (Deutsch)',
};

/* ------------------------------------------------------------------ */
/* 쿼리 빌더                                                            */
/* ------------------------------------------------------------------ */

export function buildAIQuery(
  type: AICommandType | string,
  text: string,
  language?: string,
): string {
  const trimmed = text.trim();

  switch (type) {
    case 'write':
      return (
        `다음 주제/요청에 맞는 문서를 작성해주세요. ` +
        `마크다운 형식으로 제목, 소제목, 본문을 포함하여 자세하게 작성하세요.\n\n` +
        `요청: ${trimmed}`
      );

    case 'continue':
      return (
        `다음 내용에 이어서 자연스럽게 글을 계속 작성해주세요. ` +
        `기존 문체와 주제를 유지하며 자연스럽게 이어주세요.\n\n` +
        `기존 내용:\n${trimmed}`
      );

    case 'edit':
      return (
        `다음 텍스트를 더 좋은 품질로 수정해주세요. ` +
        `문맥을 유지하면서 표현을 개선하세요.\n\n` +
        `원본 텍스트:\n${trimmed}`
      );

    case 'improve':
      return (
        `다음 텍스트를 개선해주세요. 문법, 표현, 가독성을 높여주세요. ` +
        `수정된 텍스트만 응답하세요.\n\n` +
        `원본 텍스트:\n${trimmed}`
      );

    case 'fix-grammar':
      return (
        `다음 텍스트의 문법과 맞춤법을 수정해주세요. ` +
        `의미는 변경하지 말고, 수정된 텍스트만 응답하세요.\n\n` +
        `원본 텍스트:\n${trimmed}`
      );

    case 'make-shorter':
      return (
        `다음 텍스트를 핵심 내용만 남기고 더 짧고 간결하게 줄여주세요. ` +
        `줄인 텍스트만 응답하세요.\n\n` +
        `원본 텍스트:\n${trimmed}`
      );

    case 'make-longer':
      return (
        `다음 텍스트를 더 상세하고 풍부하게 확장해주세요. ` +
        `예시나 설명을 추가하여 내용을 보강하세요.\n\n` +
        `원본 텍스트:\n${trimmed}`
      );

    case 'summarize':
      return (
        `다음 텍스트의 핵심 내용을 요약해주세요. ` +
        `가장 중요한 포인트를 간결하게 정리하세요.\n\n` +
        `원본 텍스트:\n${trimmed}`
      );

    case 'translate': {
      const langName = language ? LANG_NAMES[language] || language : '영어 (English)';
      return (
        `다음 텍스트를 ${langName}로 번역해주세요. ` +
        `자연스러운 번역만 응답하세요.\n\n` +
        `원본 텍스트:\n${trimmed}`
      );
    }

    default:
      return trimmed;
  }
}
